import { useState, useEffect, useCallback, useRef } from 'react'
import { Network } from '@capacitor/network'

const STORAGE_KEY = 'regenerai_offline_queue'

export interface QueuedMessage {
  queueId: string
  tempId: string
  conversationId: string | null
  isNewConversation?: boolean
  message: string
  displayText: string
  collectionName: string
  model: string
  queuedAt: string
}

function readQueue(): QueuedMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as QueuedMessage[]) : []
  } catch {
    return []
  }
}

function writeQueue(queue: QueuedMessage[]): void {
  try {
    if (queue.length === 0) {
      localStorage.removeItem(STORAGE_KEY)
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(queue))
    }
  } catch {
    // quota exceeded or private mode — silently skip
  }
}

type DrainFn = (item: QueuedMessage) => Promise<void>

export function useOfflineQueue(onDrain: DrainFn) {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine)
  const [pendingCount, setPendingCount] = useState(() => readQueue().length)
  const [isDraining, setIsDraining] = useState(false)
  const drainingRef = useRef(false)
  const onDrainRef = useRef(onDrain)
  onDrainRef.current = onDrain

  useEffect(() => {
    // Sync initial state via Capacitor Network plugin (works on Android WebView + browser)
    Network.getStatus().then(s => setIsOnline(s.connected))

    let cleanup: (() => void) | undefined
    Network.addListener('networkStatusChange', s => {
      setIsOnline(s.connected)
    }).then(handle => {
      cleanup = () => handle.remove()
    })
    return () => cleanup?.()
  }, [])

  // Drain queue whenever coming back online
  useEffect(() => {
    if (!isOnline || drainingRef.current) return
    const snapshot = readQueue()
    if (snapshot.length === 0) return

    drainingRef.current = true
    setIsDraining(true)
    ;(async () => {
      for (const item of snapshot) {
        try {
          await onDrainRef.current(item)
          const remaining = readQueue().filter(i => i.queueId !== item.queueId)
          writeQueue(remaining)
          setPendingCount(remaining.length)
        } catch {
          // Stop on first failure — will retry on next reconnect
          break
        }
      }
      drainingRef.current = false
      setIsDraining(false)
    })()
  }, [isOnline])

  const enqueue = useCallback((item: Omit<QueuedMessage, 'queueId' | 'queuedAt'>): string => {
    const queueId = `q_${Date.now()}_${Math.random().toString(36).slice(2)}`
    const entry: QueuedMessage = { ...item, queueId, queuedAt: new Date().toISOString() }
    const updated = [...readQueue(), entry]
    writeQueue(updated)
    setPendingCount(updated.length)
    return queueId
  }, [])

  return { isOnline, enqueue, pendingCount, isDraining }
}
